import { MKUtils } from "../common";
import { MKHttpError } from "../error";
import { MKHttpRequestOptions, MKHttpResponse } from "../../interfaces";
import { MKConstants, MKCustomError } from "../../constants";
import { MKHeaders, MKHttpMethod } from "../../alias";

/**
 * Http Request util.
 * @internal
 */
export class MKHttpRequest {
    private xhrController: XMLHttpRequest | null;

    constructor() {
        this.xhrController = null;
    }

    /**
     * Make a HTTP GET request.
     * @param url the request endpoint.
     * @param options the request options.
     * @returns Promise<MKHttpResponse>
     */
    async get(url: string, options?: MKHttpRequestOptions): Promise<MKHttpResponse> {
        return this.request(url, "GET", options);
    }

    /**
     * Make a HTTP PUT request.
     * @param url the request endpoint.
     * @param options the request options.
     * @returns Promise<MKHttpResponse>
     */
    async put(url: string, options?: MKHttpRequestOptions): Promise<MKHttpResponse> {
        return this.request(url, "PUT", options);
    }

    /**
     * Make a HTTP POST request.
     * @param url the request endpoint.
     * @param options the request options.
     * @returns Promise<MKHttpResponse>
     */
    async post(url: string, options?: MKHttpRequestOptions): Promise<MKHttpResponse> {
        return this.request(url, "POST", options);
    }

    /**
     * Make a HTTP DELETE request.
     * @param url the request endpoint.
     * @param options the request options.
     * @returns Promise<MKHttpResponse>
     */
    async delete(url: string, options?: MKHttpRequestOptions): Promise<MKHttpResponse> {
        return this.request(url, "DELETE", options);
    }

    /**
     * Abort an ongoing request.
     */
    cancelRequest() {
        if (this.xhrController) {
            this.xhrController.abort();
        }
    }

    /**
     * Make a new HTTP request.
     * @param url the request URL.
     * @param method One out of "GET" | "PUT" | "POST" | "DELETE".
     * @param options http request options.
     * @returns a promise with the response.
     */
    private async request(url: string, method: MKHttpMethod, options?: MKHttpRequestOptions): Promise<MKHttpResponse> {
        return new Promise<MKHttpResponse>((resolve, reject) => {
            const emptyPayload = {};
            const requestUrl = MKUtils.updateURLQueryParams(url, options?.queryParams);
            const requestBody = JSON.stringify(options?.body ?? emptyPayload);
            const requestTimeout = options?.timeout ?? MKConstants.HTTP_REQUEST_DEFAULT_TIMEOUT_MSECS;
            const before = Date.now();
            const xhr = new XMLHttpRequest();
            xhr.open(method, requestUrl, true); //async

            if (options?.headers) {
                Object.keys(options.headers).forEach(key => { if (options?.headers) xhr.setRequestHeader(key, options.headers[key]) });
            }

            if (options?.withCredentials) {
                xhr.withCredentials = options.withCredentials;
            }

            xhr.timeout = requestTimeout;

            xhr.onload = () => {
                const after = Date.now();
                this.xhrController = null;
                const response: MKHttpResponse = {
                    headers: this.getAllResponseHeaders(xhr.getAllResponseHeaders()),
                    ok: xhr.status >= 200 && xhr.status < 300,
                    redirected: xhr.status >= 300 && xhr.status < 400,
                    responseTime: (after - before),
                    status: xhr.status,
                    statusText: xhr.statusText,
                    url: xhr.responseURL,
                    text: () => new Promise<string>((resolve) => { resolve(xhr.responseText) }),
                    json: <T>() => JSON.parse(xhr.responseText) as T,
                }
                return resolve(response);
            };

            xhr.onerror = () => {
                this.xhrController = null;
                return reject(new MKHttpError({
                    code: MKCustomError.Http.code,
                    message: xhr.statusText ?? MKCustomError.Http.message
                }));
            };

            xhr.onabort = () => {
                this.xhrController = null;
                return reject(new MKHttpError(MKCustomError.HttpAbort));
            };

            if (method.toLowerCase() === 'post' && requestBody) {
                xhr.send(requestBody);
            } else {
                xhr.send();
            }

            this.xhrController = xhr;
        });
    }

    /**
     * Get all response headers as an object.
     * @param headers xhr.getAllResponseHeaders() string
     * @returns an object containing key-value pairs of MKHeaders.
     */
    private getAllResponseHeaders(responseHeaders: string): MKHeaders | undefined {
        if (!!responseHeaders) {
            // Convert the header string into an array of individual headers
            const arr = responseHeaders.trim().split(/[\r\n]+/);

            // Create a map of headers names to values
            const headers: MKHeaders = {};
            arr.forEach((line) => {
                const parts = line.split(": ");
                const key = parts.shift()?.toLowerCase();
                const value = parts.join(": ");
                if (key) {
                    headers[key] = value;
                }
            });
            return headers;
        }
        return undefined;
    }
}